import { collectAdoptionDiagnostics, convertTwinToPlan } from '@repo/ui';
import { dgqFetchTwinOutcome, MIDDLEWARE_REQUEST_TIMEOUT_MS } from '@repo/front-end-services';
import type { FloorPlannerLayout } from '@repo/frontend-types';

import { PLAN_NAME } from './constants';
import { activationOrgErrorMessage, resolveActivationOrg } from './resolveActivationOrg';

/**
 * The twin's RUNNING model, read from dGraph and reversed into plan JSON — one pipeline, two
 * readers (`camera-observer-setup-attention`).
 *
 * It was inline in `useLiveTwinPlan` (the Floor Plan chooser's *Start from this twin* option). The
 * Observers stage now needs the same layout as the reference geometry for "has this moved since its
 * observer was drawn", and a second copy of org resolution + fetch + convert is how the two would
 * drift. Both hooks call this and map the outcome onto their own state.
 *
 * ## Three answers, kept apart
 *
 * `none` is a twin that has never been taken live — a first activation, or a chooser option that
 * cannot be offered — and is not a failure. `failed` is a read that broke, and carries a sentence a
 * person can act on. Collapsing the two would tell an operator their twin has no model when in fact
 * the read broke; in the chooser that leads to a blank canvas destroying the model on Go Live, and
 * on the Observers stage it would silently hide every "observer needs checking" state.
 *
 * ## Bounds
 *
 * `docs/coding-standards.md` names this dGraph read as one of the unbounded browser calls, with the
 * instruction to apply `MIDDLEWARE_REQUEST_TIMEOUT_MS` when touching it. Applied to all three reads
 * this makes — the twin record, the organisation list and the dGraph query: the caller's
 * `AbortSignal` is the authoritative deadline and the 30 s timeout the secondary guard on each. One
 * attempt; a retry is the operator's, never automatic.
 */
export type LiveTwinLayoutOutcome =
  | {
      status: 'ready';
      layout: FloorPlannerLayout;
      /** The chooser's sentence about what could not be imported, when anything could not. */
      warning?: string;
      /** How many entities were skipped for having no identity; each host phrases its own line. */
      skipped: number;
    }
  | { status: 'none' }
  | { status: 'failed'; reason: string }
  /** The caller's signal was aborted mid-flight. Nothing was converted; the caller sets no state. */
  | { status: 'cancelled' };

export interface FetchLiveTwinLayoutParams {
  middlewareApi: string;
  accessToken: string;
  twinBid: string;
  /** The org the OPERATOR has selected — an input to the check, never the answer. */
  selectedOrgBid: string;
  signal?: AbortSignal;
  /** Where this read is reported from, for the console prefix. */
  logTag?: string;
}

/** Copy for the states a user can reach. Exported so both hooks' tests can pin the same words. */
export const LIVE_LAYOUT_REASON = {
  readFailed: "Could not read this twin's current model. Reload the page and try again.",
} as const;

/**
 * The one throw `convertTwinToPlan` makes on purpose, with copy written for an operator. Any other
 * throw is a defect in the conversion and is NOT surfaced verbatim: its message would be a
 * TypeError in a `role="alert"` banner, which leaks internals and tells the operator nothing.
 */
const AUTHORED_CONVERTER_MESSAGE = 'Twin has no spatial geometry to import into a plan.';

export const fetchLiveTwinLayout = async ({
  middlewareApi,
  accessToken,
  twinBid,
  selectedOrgBid,
  signal,
  logTag = 'live-model',
}: FetchLiveTwinLayoutParams): Promise<LiveTwinLayoutOutcome> => {
  // 1. WHOSE NAMESPACE? Resolved from the twin's own `organisation_id`, never from the operator's
  //    selection — a TL_ADMIN sees every twin in every org, so what they have selected is not
  //    evidence of which org a twin belongs to. This is also why `ORG_BID` (the literal string
  //    'ONBOARDING', exported beside `PLAN_NAME` from `./constants`) must not be imported here: it
  //    is the planner session's bookkeeping label, it would type-check, and passing it to the
  //    fetch below would read a dGraph namespace that does not exist.
  //    Bounded like the dGraph read below: the same signal and the same timeout on both reads.
  const bounds = { signal, timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS };
  const orgOutcome = await resolveActivationOrg({
    middlewareApi,
    accessToken,
    twinBid,
    selectedOrgBid,
    options: bounds,
  });
  if (signal?.aborted) return { status: 'cancelled' };
  if (orgOutcome.status !== 'ok') {
    if (orgOutcome.status === 'failed') {
      console.error(`[${logTag}] could not resolve the twin’s organisation:`, orgOutcome.reason);
    }
    return { status: 'failed', reason: activationOrgErrorMessage(orgOutcome)! };
  }
  const orgBID = orgOutcome.orgBid;

  // 2. Fetch the live tree — bounded, and with `failed` kept apart from `absent`.
  //    An organisation with no dGraph namespace holds no entities at all: `dGraphQuery` resolves
  //    null for it and the outcome wrapper reads that as `failed`, which is right for the delete
  //    paths that wrap it and wrong here — a never-activated org has no model, which is `none`,
  //    not an error with a Retry. The classification arrives through `onFailure` (the wrapper's
  //    own header says why it is a callback and not a fourth status). Found by Copilot on the PR.
  let namespaceAbsent = false;
  const outcome = await dgqFetchTwinOutcome(middlewareApi, accessToken, orgBID, twinBid, {
    ...bounds,
    onFailure: failure => {
      namespaceAbsent = failure.namespaceAbsent;
    },
  });
  // Checked BEFORE converting: a several-hundred-entity tree can land after the component that
  // asked for it has gone, and converting it then is wasted work on a dead component.
  if (signal?.aborted) return { status: 'cancelled' };
  if (outcome.status === 'absent' || (outcome.status === 'failed' && namespaceAbsent)) {
    return { status: 'none' };
  }
  if (outcome.status === 'failed') {
    console.error(`[${logTag}] could not read the twin model:`, outcome.error);
    return { status: 'failed', reason: LIVE_LAYOUT_REASON.readFailed };
  }

  // 3. Reverse the tree into plan JSON. The converter THROWS by design on a twin with no spatial
  //    geometry; that throw is a reason, not a crash.
  try {
    const diagnostics = collectAdoptionDiagnostics();
    const layout = convertTwinToPlan(outcome.twin, {
      twinBID: twinBid,
      twinName: PLAN_NAME,
      orgBID,
      onDiagnostic: diagnostics.onDiagnostic,
    });
    if (diagnostics.messages.length > 0) {
      console.info(`[${logTag}] entities not compared:`, diagnostics.messages);
    }
    return {
      status: 'ready',
      layout,
      warning: diagnostics.summary(),
      skipped: diagnostics.skippedCount(),
    };
  } catch (err) {
    console.error(`[${logTag}] could not convert the live model into a plan:`, err);
    return {
      status: 'failed',
      reason:
        err instanceof Error && err.message === AUTHORED_CONVERTER_MESSAGE
          ? err.message
          : LIVE_LAYOUT_REASON.readFailed,
    };
  }
};
